import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Badge } from '../../../components/ui/badge';
import { useInventory } from '../../../features/inventory/hooks/useInventory';
import { InventoryItem } from '../../../features/inventory/types/inventory';

export interface InventoryUsage {
  itemId: string;
  quantity: number;
  notes?: string;
}

interface InventoryUsageFormProps {
  value: InventoryUsage[];
  onChange: (usage: InventoryUsage[]) => void;
}

export function InventoryUsageForm({ value, onChange }: InventoryUsageFormProps) {
  const { data: inventory } = useInventory();
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const selectedItem = inventory?.find((item: InventoryItem) => item.id === selectedItemId);

  const handleAddItem = () => {
    if (!selectedItemId || !quantity) return;

    const newUsage: InventoryUsage = {
      itemId: selectedItemId,
      quantity: Number(quantity),
      notes: notes.trim() || undefined
    };

    onChange([...value, newUsage]);
    
    // Reset form
    setSelectedItemId('');
    setQuantity('');
    setNotes('');
  };

  const handleRemoveItem = (index: number) => {
    const newUsage = [...value];
    newUsage.splice(index, 1);
    onChange(newUsage);
  };

  const getItemById = (id: string): InventoryItem | undefined => {
    return inventory?.find((item: InventoryItem) => item.id === id);
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b">
          <CardTitle>Add Items Used</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Item</Label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an item" />
                </SelectTrigger>
                <SelectContent>
                  {inventory?.filter((item: InventoryItem) => item.id).map((item: InventoryItem) => (
                    <SelectItem key={item.id} value={item.id!}>
                      <div className="flex items-center justify-between w-full">
                        <span>{item.name}</span>
                        <Badge variant="secondary" className="ml-2">
                          {item.quantity} {item.unit}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedItem && (
                <p className="text-sm text-muted-foreground">
                  Available: {selectedItem.quantity} {selectedItem.unit}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Quantity Used</Label>
              <Input
                type="number"
                min="1"
                max={selectedItem?.quantity || 999999}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={`Enter quantity in ${selectedItem?.unit || 'units'}`}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about usage..."
              />
            </div>
          </div>
          <Button
            onClick={handleAddItem}
            disabled={!selectedItemId || !quantity}
            className="mt-4"
          >
            Add Item
          </Button>
        </CardContent>
      </Card>

      {value.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b">
            <CardTitle>Items Used</CardTitle>
          </CardHeader>
          <ScrollArea className="h-[300px]">
            <CardContent className="p-6">
              <div className="space-y-4">
                {value.map((usage, index) => {
                  const item = getItemById(usage.itemId);
                  if (!item) return null;

                  return (
                    <div
                      key={`${usage.itemId}-${index}`}
                      className="flex items-start justify-between p-4 rounded-lg border"
                    >
                      <div className="space-y-1">
                        <h4 className="font-medium">{item.name}</h4>
                        <p className="text-sm text-gray-600">
                          Quantity: {usage.quantity} {item.unit}
                        </p>
                        {usage.notes && (
                          <p className="text-sm text-gray-500">
                            Note: {usage.notes}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                )}
              </div>
            </CardContent>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}
